import React, { useState, useCallback, useRef, useEffect, Suspense } from "react";
import TopBar from "./components/TopBar";
import SetupPage from "./components/SetupPage";
import Sidebar from "./components/Sidebar";
import CadCanvas from "./components/CadCanvas";
import ExportPage from "./components/ExportPage";
import SketchImport from "./components/SketchImport";
import PromptImport from './components/PromptImport';
import HelpPage from "./components/HelpPage";   // Knowledge base page
import TermsModal, { hasAcceptedCurrentTerms } from "./components/TermsModal";   // ToS gate
import ViewModeToggle from "./components/ViewModeToggle";   // 2D/3D floating pill
import { CATALOG, migrateEquipmentSizes } from "./data/catalog";
import { uid } from "./utils/cadUtils";
import { migrateShop, migrateDoors, makeFreeShop, isSelfIntersecting } from "./utils/shopGeometry";   // Shop + doors migration helpers
import { T } from "./theme";   // Foothold: theme.js exists for future theming work
import { useUndoableState } from "./useUndoableState";

// Lazy-load Canvas3D so the ~600KB Three.js bundle stays out of initial page load.
// Only downloads when user first clicks "3D View" pill.
const Canvas3D = React.lazy(() => import("./components/Canvas3D"));

const EMPTY_FORM = {
  customerName: "",
  companyName: "",
  address: "",
  city: "",
  phone: "",
  email: "",
  drawnBy: "",
  projectName: "",
  notes: "",
  ceilingHeight: "",
  concreteThickness: "",
  concreteCondition: "",
  // Drawing sheet export (v4.6)
  drawingNumber: "",
  revision: "",
  exportSettings: {},   // template / paper / toggles / logo — see export/sheetBuilder
};

// ─── Autosave ────────────────────────────────────────────────────
// The current project is mirrored to localStorage (debounced) so a refresh,
// crash or closed tab doesn't wipe the layout. On the next visit the user is
// asked whether to restore it. This is a browser-only backup — Save Project
// (a .sbpro file) is still the real copy, so "unsaved" means "not in a file".
const AUTOSAVE_KEY = "sb_autosave_v1";
const AUTOSAVE_DELAY_MS = 600;

// Starting shop, shared by useState and the "fresh project" baseline below
const INITIAL_SHOP = migrateShop({ w: 60, h: 40, unit: "ft" });

// One string that changes whenever anything that goes into a .sbpro file
// changes. Compared against the last saved/loaded value to show "unsaved".
const projectSig = (p) =>
  JSON.stringify([p.shop, p.doors, p.placed, p.drains, p.dimensions, p.exportForm]);

const FRESH_SIG = projectSig({
  shop: INITIAL_SHOP, doors: [], placed: [], drains: [], dimensions: [], exportForm: EMPTY_FORM,
});

// Returns the autosaved project only if it's worth offering back (has
// equipment, doors or a project name) — an empty default isn't.
const readAutosave = () => {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !d.shop || !Array.isArray(d.placed)) return null;
    const hasContent =
      d.placed.length > 0 ||
      (d.doors || []).length > 0 ||
      ((d.exportForm && d.exportForm.projectName) || "").trim() !== "";
    return hasContent ? d : null;
  } catch {
    return null; // corrupted or storage blocked — just start fresh
  }
};

// "just now" / "5 min ago" / "3 hr ago" / a date — for the restore prompt
const timeAgo = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(mins)) return "earlier";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} hr ago`;
  return new Date(iso).toLocaleDateString();
};

const makeRoom = (name, cx, cy, fw, fh) => ({
  uid: uid(),
  isRoom: true,
  id: "room",
  name,
  label: name,
  cx,
  cy,
  fw,
  fh,
  scaleX: 1,
  scaleY: 1,
  rot: 0,
  color: "rgba(255,229,0,0.08)",
  border: "rgba(255,229,0,0.6)",
  _dragging: false,
});

export default function App() {
  // ─── Terms gate ────────────────────────────────────────────────
  // Initialized from localStorage — if user already accepted current version,
  // they skip the modal entirely. If terms version bumps, they get re-prompted.
  const [termsAccepted, setTermsAccepted] = useState(hasAcceptedCurrentTerms());

  const [step, setStep] = useState("setup");
  // Shop is wrapped through migrateShop() so it always has the new
  // shapeType/vertices/walls fields, even when starting from the legacy {w, h, unit}.
  const [shop, setShop] = useState(INITIAL_SHOP);

  // ─── View mode: 2D editing vs 3D preview ───────────────────────
  // Flipped by the ViewModeToggle pill at the bottom of the canvas area.
  // "2d" = CadCanvas (full edit), "3d" = Canvas3D (read-only preview).
  // Always reset to 2d when going to export so PDF generation sees the SVG.
  const [viewMode, setViewMode] = useState("2d");

  // ─── Undoable state ────────────────────────────────────────────
  // placed/doors/drains/dimensions live in this hook, which tracks a snapshot
  // history. commitHistory() marks "user finished an action"; undo/redo on Ctrl+Z/Y.
  const {
    placed, doors, drains, dimensions,
    setPlaced, setDoors, setDrains, setDimensions,
    commitHistory, undo, redo, resetHistory,
    canUndo, canRedo,
  } = useUndoableState({ placed: [], doors: [], drains: [], dimensions: [] });

  const [selected, setSelected] = useState([]);
  // ─── Vehicle approach simulation ───────────────────────────────
  // Which vehicle class the "simulate approach" toggle (per selected
  // lift) uses. Shared between CadCanvas and Canvas3D so switching the
  // 2D/3D view mid-simulation shows the same vehicle, not a reset one.
  const [simVehicleKey, setSimVehicleKey] = useState('car');
  // Which overhead door the simulation approaches from. null = "auto,"
  // meaning fall back to whichever overhead door is nearest the selected
  // lift — the same behavior as before manual selection existed. Set to a
  // door's id when the user explicitly picks one (e.g. because the nearest
  // door is actually blocked by an interior wall, as in the Dam's Ford
  // blueprint's "top door").
  const [simDoorId, setSimDoorId] = useState(null);
  // 'auto' = let the solver pick the path (default, as before). 'manual' =
  // the user builds the path themselves as a chain of waypoints, each with
  // its own forward/reverse leg — an escape hatch for real approaches
  // (tight truck maneuvers around neighboring lifts, etc.) the bounded
  // auto-search can't find on its own.
  const [simPathMode, setSimPathMode] = useState('auto');
  // Ordered list of manually-placed waypoints — [] until the user adds
  // the first one. Each is { x, y, heading, gear }, where gear is the
  // forward/reverse direction of the leg ARRIVING at that point (from the
  // door, or from the previous waypoint). Shared at App level so flipping
  // to 3D previews the exact same chain, not a blank one.
  const [simManualWaypoints, setSimManualWaypoints] = useState([]);
  // Gear for the final leg, from the last waypoint (or the door, if there
  // are no waypoints yet) into the lift itself.
  const [simManualGoalGear, setSimManualGoalGear] = useState('reverse');
  // Which of the lift's two symmetric ends the manual chain solves
  // toward — shared so flipping it in 2D also flips the 3D preview.
  const [simManualGoalFlip, setSimManualGoalFlip] = useState(false);
  // Playback speed multiplier for the approach animation — shared so 2D
  // and 3D scrub at the same rate when you switch views mid-playback.
  const [simSpeed, setSimSpeed] = useState(1);
  const [selectedDrain, setSelectedDrain] = useState(null);
  const [selectedDim, setSelectedDim] = useState(null);
  const [exportForm, setExportForm] = useState(EMPTY_FORM);
  const [toast, setToast] = useState(null);

  // ─── Autosave / unsaved-changes state ──────────────────────────
  // pendingRestore = an autosaved project waiting for the user to choose
  // Restore or Start new. While it's set, autosave is paused so the fresh
  // empty state can't overwrite the old layout before they decide.
  const [pendingRestore, setPendingRestore] = useState(() => readAutosave());
  // Signature of the project as of the last file save/load. "Dirty" = the
  // current project differs from it. Starts as the empty default project.
  const savedSigRef = useRef(FRESH_SIG);
  const [isDirty, setIsDirty] = useState(false);
  // True once the user has saved or loaded a file this session (for the "Saved" label)
  const [hasFile, setHasFile] = useState(false);
  const autosaveWarnedRef = useRef(false);   // show the "storage full" toast only once
  const [showSketchImport, setShowSketchImport] = useState(false);
  const [showPromptImport, setShowPromptImport] = useState(false);
  // ─── Free-form room drawing mode ───────────────────────────────
  // When true, CadCanvas captures clicks as polygon vertices rather than
  // selecting items. Lifted to App level so Sidebar's "Add Free-form Room"
  // button can toggle it, while CadCanvas owns the actual drawing UX.
  const [roomDrawMode, setRoomDrawMode] = useState(false);
  const svgRef = useRef(null);
  // Ref into CadCanvas itself (not just its inner SVG) — lets us call
  // resetZoom() right before export so the exported view is always fitted
  // and centered, regardless of what zoom/pan the user left the editor at.
  const cadCanvasRef = useRef(null);

  // Step Help was opened from, so its Back button returns there (not always to the editor)
  const prevStepRef = useRef("build");
  // Single toast timer — clear the old one first so a second toast gets its
  // full 3.5s instead of being wiped by the first toast's leftover timer.
  const toastTimerRef = useRef(null);

  const showToast = (msg, type = "error") => {
    setToast({ msg, type });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };

  // Everything that goes into a .sbpro file (and the browser autosave)
  const buildProjectData = () => ({
    version: 2,                          // schema version (NOT project version)
    projectName: (exportForm.projectName || "").trim() || "Untitled Project",
    shop,
    doors,
    placed,
    drains,
    dimensions,
    exportForm,
  });

  // Mirror of pendingRestore for the once-registered pagehide handler
  const pendingRestoreRef = useRef(pendingRestore);
  pendingRestoreRef.current = pendingRestore;

  // Latest data in a ref so the pagehide handler (registered once) can
  // flush an autosave with current values instead of stale ones
  const latestRef = useRef(null);
  latestRef.current = { data: buildProjectData(), step, isDirty };

  const writeAutosave = (data, stepNow, dirtyNow) => {
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({
        ...data,
        savedAt: new Date().toISOString(),
        step: stepNow,
        matchesFile: !dirtyNow,   // restore can tell whether a file copy already exists
      }));
    } catch {
      // Quota exceeded or storage blocked (private mode) — warn once, keep working
      if (!autosaveWarnedRef.current) {
        autosaveWarnedRef.current = true;
        showToast("Browser autosave isn't available here — use Save Project to keep your work.");
      }
    }
  };

  const writeAutosaveRef = useRef(writeAutosave);
  writeAutosaveRef.current = writeAutosave;

  // Debounced: recompute "unsaved" + write the autosave 600ms after the last
  // change. Debouncing keeps drags smooth (no stringify on every mouse frame).
  useEffect(() => {
    if (pendingRestore) return;
    const t = setTimeout(() => {
      // Read through refs: always the newest values, and no function deps
      const { data, step: stepNow } = latestRef.current;
      const dirty = projectSig(data) !== savedSigRef.current;
      setIsDirty(dirty);
      writeAutosaveRef.current(data, stepNow, dirty);
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(t);
    // The listed values are the triggers; the timer reads them via latestRef
  }, [shop, doors, placed, drains, dimensions, exportForm, step, pendingRestore]);

  // Closing/refreshing the tab: flush the autosave right away (the debounce
  // may not have fired yet), and warn if the work isn't in a file.
  useEffect(() => {
    const onPageHide = () => {
      const l = latestRef.current;
      if (l && !pendingRestoreRef.current) writeAutosaveRef.current(l.data, l.step, l.isDirty);
    };
    const onBeforeUnload = (e) => {
      if (!latestRef.current?.isDirty) return;
      e.preventDefault();
      e.returnValue = "";   // browsers show their own generic "leave site?" text
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);   // registered once; reads live values through refs
  const goToExport = () => {
    setSelected([]);
    setSelectedDrain(null);
    setSelectedDim(null);
    setViewMode("2d");   // Export PDF generator reads the SVG canvas, so force 2D before switching
    // Reset zoom/pan to defaults BEFORE switching to export. Without this,
    // exports capture whatever zoom/pan state the user happened to leave the
    // editor at (very likely non-default for big/complex shops), which is
    // the root cause of exports showing the shop cut off or off-center —
    // the export math assumes a fitted, centered view, but the live SVG
    // wasn't actually in that state.
    cadCanvasRef.current?.resetZoom();
    setTimeout(() => {
      const svgEl = svgRef.current;
      if (svgEl) {
        svgEl.setAttribute("width", svgEl.clientWidth);
        svgEl.setAttribute("height", svgEl.clientHeight);
      }
      setStep("export");
    }, 100);
  };

  // Can the user leave Setup? A free-form shop must be a closed polygon
  // with no crossing walls (this used to live only on the page's own
  // Open Floor Plan button; now the stepper and Next button check it too).
  const canLeaveSetup = (() => {
    if ((shop.shapeType || "rect") !== "free") return shop.w > 0 && shop.h > 0;
    const v = shop.vertices || [];
    const closed = v.length >= 3 && shop.walls && shop.walls.length === v.length;
    return closed && !isSelfIntersecting(v);
  })();

  const applyTemplate = useCallback((template) => {
    // Migrate the template's shop so it gets the new fields populated
    const migratedShop = migrateShop(template.shop);
    setShop(migratedShop);
    // Build doors with fresh IDs, then migrate to wall-attached format
    const newDoorsRaw = template.doors.map((d) => ({
      ...d,
      id: Date.now() + Math.random(),
      width: d.width ?? (d.type === "overhead" ? 10 : 4),
    }));
    const newDoors = migrateDoors(newDoorsRaw, migratedShop);
    const items = template.equipment
      .map((e) => {
        const eq = CATALOG.find((c) => c.id === e.id);
        if (!eq) return null;
        return {
          ...JSON.parse(JSON.stringify(eq)),
          uid: uid(),
          cx: e.cx,
          cy: e.cy,
          rot: e.rot,
          scaleX: e.scaleX,
          scaleY: e.scaleY,
          label: e.label || "",
        };
      })
      .filter(Boolean);
    const allItems = [...items];
    if (template.office) {
      const { corner, w, h } = template.office;
      // Use the migrated shop's w/h for office corner math (stays compatible)
      const s = migratedShop;
      const corners = {
        NW: { cx: w / 2, cy: h / 2 },
        NE: { cx: s.w - w / 2, cy: h / 2 },
        SW: { cx: w / 2, cy: s.h - h / 2 },
        SE: { cx: s.w - w / 2, cy: s.h - h / 2 },
      };
      const pos = corners[corner] || corners.NW;
      allItems.unshift(makeRoom("Office", pos.cx, pos.cy, w, h));
    }
    // resetHistory wipes the undo stack — you can't undo back across a template load
    resetHistory({
      placed: allItems,
      doors: newDoors,
      drains: [],
      dimensions: [],
    });
    setSelected([]);
    setSelectedDrain(null);
    setSelectedDim(null);
    setSimDoorId(null);
    setSimManualWaypoints([]);
    setStep("build");
    showToast(`Template "${template.name}" applied — move or add equipment to suit your shop.`, "success");
  }, [resetHistory]);

  // Template picked on Setup — confirm first if it would replace real work
  const handlePickTemplate = (template) => {
    const hasWork = placed.length > 0 || doors.length > 0;
    if (hasWork && !window.confirm(
      `Start from "${template.name}"? This replaces the current shop, doors and equipment.`
    )) return;
    applyTemplate(template);
  };

  // Handles import payloads from BOTH SketchImport and PromptImport.
  // Two payload shapes:
  //   1. POLYGON shape: { shape: 'polygon', vertices, doors: [{wallId, offset, width, type}], office: null, placed }
  //      - doors are PRE-MIGRATED to wall-attached format by SketchImport
  //      - office is always null for polygons (corner math doesn't generalize)
  //      - shop is built directly from vertices via makeFreeShop()
  //   2. RECT shape (legacy): { shop: {w, h, unit}, doors: [{wall, pos, type, width}], office, placed }
  //      - this is what PromptImport always sends, and what SketchImport sends for rect detections
  //      - doors are migrated here via migrateDoors()
  const handleSketchImport = (payload) => {
    let migratedShop;
    let newDoors;
    let officeData;

    if (payload.shape === 'polygon' && Array.isArray(payload.vertices)) {
      // Polygon path — vertices come pre-normalized (NW at 0,0) from SketchImport
      migratedShop = makeFreeShop(payload.vertices, 'ft');
      // Doors are already in wall-attached format — just stamp fresh IDs
      // in case the import-side IDs collided (Date.now()+random is fine but cheap to refresh)
      newDoors = (payload.doors || []).map((d) => ({
        ...d,
        id: d.id || (Date.now() + Math.random()),
      }));
      officeData = null;   // skipped for polygons
    } else {
      // Rect path — legacy contract, identical to original behavior
      migratedShop = migrateShop(payload.shop);
      const newDoorsRaw = (payload.doors || []).map((door) => ({
        ...door,
        id: Date.now() + Math.random(),
        width: door.width ?? (door.type === "overhead" ? 10 : 4),
      }));
      newDoors = migrateDoors(newDoorsRaw, migratedShop);
      officeData = payload.office || null;
    }

    setShop(migratedShop);
    const allItems = (payload.placed || []).map((item) => ({
      ...item,
      uid: uid(),
      label: item.label || "",
    }));
    if (officeData) {
      const { corner, w, h } = officeData;
      const shopForCorners = migratedShop;
      const corners = {
        NW: { cx: w / 2, cy: h / 2 },
        NE: { cx: shopForCorners.w - w / 2, cy: h / 2 },
        SW: { cx: w / 2, cy: shopForCorners.h - h / 2 },
        SE: { cx: shopForCorners.w - w / 2, cy: shopForCorners.h - h / 2 },
      };
      const pos = corners[corner] || corners.NW;
      allItems.unshift(makeRoom("Office", pos.cx, pos.cy, w, h));
    }
    // Sketch/prompt import is a hard reset — old undo history doesn't apply
    resetHistory({
      placed: allItems,
      doors: newDoors,
      drains: [],
      dimensions: [],
    });
    setSelected([]);
    setSelectedDrain(null);
    setSelectedDim(null);
    setStep("build");
    showToast(
      payload.shape === 'polygon'
        ? "Polygon shop imported — review and adjust the layout."
        : "Sketch imported — review and adjust the layout.",
      "success"
    );
  };

  const handleSave = () => {
    // Pull project name from exportForm — single source of truth, set on the
    // Setup page and optionally tweaked on the Export page. Falls back to a
    // generic name if the user hasn't filled it in.
    const project = buildProjectData();
    const rawName = project.projectName;
    const data = { ...project, savedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // Sanitize project name for filesystem safety, then append a YYYY-MM-DD_HHmm
    // timestamp so each save is uniquely named without overwriting prior saves.
    const safeName =
      rawName
        .replace(/[^a-z0-9\s\-_]/gi, "")
        .trim()
        .replace(/\s+/g, "_") || "Untitled_Project";
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;

    link.download = `${safeName}_${stamp}.sbpro`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    // This exact state is now in a file — clear the "unsaved" flag
    savedSigRef.current = projectSig(project);
    setIsDirty(false);
    setHasFile(true);
    showToast(`"${rawName}" saved!`, "success");
  };

  // Puts a project (from a .sbpro file or the browser autosave) on screen.
  // markSaved = true when this state is known to match a file on disk.
  const applyProjectData = (data, { markSaved, step: nextStep = "build" }) => {
    // migrateShop handles both legacy {w,h,unit} and new {shapeType:'free',vertices} formats
    const migratedShop = migrateShop(data.shop);
    // Always start from a blank form so a file without customer details
    // doesn't inherit the previous project's customer
    const form = { ...EMPTY_FORM, ...(data.exportForm || {}) };
    // Build the final placed[] in one go so resetHistory gets the
    // complete state. The legacy office-fallback prepends a Room when
    // the saved file references data.office but doesn't include the room.
    // Old placeholder sizes (8×8 ft compressor etc.) → real footprints,
    // for items nobody resized by hand (see catalog.migrateEquipmentSizes)
    let finalPlaced = migrateEquipmentSizes(data.placed || []);
    if (data.office && !finalPlaced.some((p) => p.isRoom)) {
      const { corner, w, h } = data.office;
      const s = migratedShop;
      const corners = {
        NW: { cx: w / 2, cy: h / 2 },
        NE: { cx: s.w - w / 2, cy: h / 2 },
        SW: { cx: w / 2, cy: s.h - h / 2 },
        SE: { cx: s.w - w / 2, cy: s.h - h / 2 },
      };
      const pos = corners[corner] || corners.NW;
      finalPlaced = [makeRoom("Office", pos.cx, pos.cy, w, h), ...finalPlaced];
    }
    // Migrate doors from old N/S/E/W format to new wall-attached format.
    const finalDoors = migrateDoors(data.doors || [], migratedShop);
    const drainsIn = data.drains || [];
    const dimsIn = data.dimensions || [];

    setShop(migratedShop);
    setExportForm(form);
    // Loading a project wipes undo history — pre-load undo would be confusing.
    resetHistory({ placed: finalPlaced, doors: finalDoors, drains: drainsIn, dimensions: dimsIn });
    setSelected([]);
    setSelectedDrain(null);
    setSelectedDim(null);
    // Approach-sim choices point at door ids from the old project — clear them
    setSimDoorId(null);
    setSimManualWaypoints([]);
    setStep(nextStep);

    if (markSaved) {
      savedSigRef.current = projectSig({
        shop: migratedShop, doors: finalDoors, placed: finalPlaced,
        drains: drainsIn, dimensions: dimsIn, exportForm: form,
      });
      setIsDirty(false);
    }
  };

  const handleLoad = (file) => {
    // Loading replaces everything on screen — check first if that work isn't in a file
    if (isDirty && !window.confirm(
      "You have unsaved changes. Loading a project will replace the current layout.\n\nContinue without saving?"
    )) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.shop || !data.placed) throw new Error("Invalid file");
        applyProjectData(data, { markSaved: true });
        setHasFile(true);
        showToast(`"${data.projectName || "Project"}" loaded!`, "success");
      } catch {
        showToast("Failed to load — invalid or corrupted file.", "error");
      }
    };
    reader.readAsText(file);
  };

  // Restore prompt → "Restore": bring back the autosaved layout. It only
  // counts as saved if it matched a file when it was autosaved.
  const handleRestore = () => {
    const d = pendingRestore;
    const nextStep = d.step === "setup" ? "setup" : "build";   // help/export → editor
    applyProjectData(d, { markSaved: !!d.matchesFile, step: nextStep });
    setPendingRestore(null);
    showToast(`Restored "${d.projectName || "Untitled Project"}".`, "success");
  };

  // Restore prompt → "Start new": keep the fresh project; the next autosave
  // overwrites the old one.
  const handleStartFresh = () => setPendingRestore(null);

  // ─── Terms gate ────────────────────────────────────────────────
  // Block the entire app behind the modal until accepted. We return
  // a minimal styled wrapper (matches app bg) so there's no flash of
  // unstyled content behind the overlay.
  if (!termsAccepted) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          background: T.appBg,
          fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
        }}
      >
        <TermsModal onAccept={() => setTermsAccepted(true)} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        background: T.appBg,                                  // Single foothold: pulls navy bg from theme.js
        fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
      }}
    >
      <TopBar
        step={step}
        shop={shop}
        placed={placed}
        doors={doors}
        drains={drains}
        exportForm={exportForm}
        onNav={(s) => {
          // Back from Help: return to the step it was opened from
          const target = s === "back" ? prevStepRef.current : s;
          if (s === "help" && step !== "help") prevStepRef.current = step;
          if (target === "export") goToExport();
          else setStep(target);
        }}
        onSave={handleSave}
        onLoad={handleLoad}
        isDirty={isDirty}
        hasFile={hasFile}
        canLeaveSetup={canLeaveSetup}
        onSketchImport={() => setShowSketchImport(true)}
        onPromptImport={() => setShowPromptImport(true)}
      />

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background:
              toast.type === "success"
                ? "rgba(0,140,80,.95)"
                : "rgba(180,40,40,.95)",
            color: "#fff",
            padding: "10px 22px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "0 4px 20px rgba(0,0,0,.5)",
            zIndex: 999,
            pointerEvents: "none",
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* Restore prompt — shown once on open when the browser has an autosaved
          layout. Blocking on purpose: autosave is paused until they choose,
          so nothing overwrites the old layout by accident. */}
      {pendingRestore && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#0a1628", border: "1px solid #1e3a54", borderRadius: 12,
            padding: 26, width: 400, boxShadow: "0 8px 40px rgba(0,0,0,.6)",
          }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#e8f4ff" }}>
              Pick up where you left off?
            </div>
            <div style={{ fontSize: 13, color: "#8aa8c5", marginTop: 8, lineHeight: 1.6 }}>
              Your last layout was saved in this browser {timeAgo(pendingRestore.savedAt)}.
            </div>
            {/* Summary card so they recognise which project it is */}
            <div style={{
              marginTop: 14, padding: "12px 14px", background: "#07111e",
              border: "1px solid #162840", borderRadius: 8, fontSize: 13, color: "#c8dff0", lineHeight: 1.6,
            }}>
              <div style={{ fontWeight: 600 }}>{pendingRestore.projectName || "Untitled Project"}</div>
              <div style={{ color: "#8aa8c5", fontSize: 12 }}>
                {pendingRestore.shop.w}×{pendingRestore.shop.h} ft ·{" "}
                {pendingRestore.placed.filter((p) => !p.isRoom).length} equipment ·{" "}
                {(pendingRestore.doors || []).length} doors
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handleStartFresh} style={{
                flex: 1, padding: "10px 0", background: "transparent", border: "1px solid #1e3a54",
                borderRadius: 8, color: "#8aa8c5", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>
                Start new
              </button>
              <button onClick={handleRestore} autoFocus style={{
                flex: 1, padding: "10px 0", background: "linear-gradient(135deg,#0055aa,#0077dd)", border: "none",
                borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>
                Restore layout
              </button>
            </div>
          </div>
        </div>
      )}

      {showSketchImport && (
        <SketchImport
          onImport={handleSketchImport}
          onClose={() => setShowSketchImport(false)}
        />
      )}

      {showPromptImport && (
        <PromptImport
          onImport={handleSketchImport}
          onClose={() => setShowPromptImport(false)}
          currentLayout={{ shop, doors, placed }}
        />
      )}

      {step === "setup" && (
        <SetupPage
          shop={shop}
          setShop={setShop}
          doors={doors}
          setDoors={setDoors}
          form={exportForm}
          setForm={setExportForm}
          onNext={() => setStep("build")}
          onPickTemplate={handlePickTemplate}
        />
      )}

      <div
        style={{
          display: step === "build" || step === "export" ? "flex" : "none",
          flex: 1,
          overflow: "hidden",
          minWidth: 0,
          visibility: step === "export" ? "hidden" : "visible",
          position: step === "export" ? "absolute" : "relative",
          width: "100%",
          height: "calc(100vh - 48px)",
        }}
      >
        <Sidebar
          placed={placed}
          selected={selected}
          setPlaced={setPlaced}
          setSelected={setSelected}
          drains={drains}
          setDrains={setDrains}
          selectedDrain={selectedDrain}
          setSelectedDrain={setSelectedDrain}
          dimensions={dimensions}
          setDimensions={setDimensions}
          selectedDim={selectedDim}
          setSelectedDim={setSelectedDim}
          shop={shop}
          commitHistory={commitHistory}
          roomDrawMode={roomDrawMode}
          setRoomDrawMode={setRoomDrawMode}
          onAddItem={(id) => {
            // Click-to-add places items on the 2D plan; flip back from 3D so the user sees it
            setViewMode("2d");
            cadCanvasRef.current?.addCatalogItem(id);
          }}
        />

        {/* Canvas area — relative-positioned wrapper so the floating
            ViewModeToggle pill can anchor to its bottom edge. */}
        <div style={{ flex: 1, position: "relative", minWidth: 0, overflow: "hidden" }}>
          {/* 2D canvas — always mounted so its internal state (zoom, pan, etc.)
              persists when user flips to 3D and back. Hidden via display:none. */}
          <div style={{
            width: "100%",
            height: "100%",
            display: viewMode === "2d" ? "block" : "none",
          }}>
            <CadCanvas
              ref={cadCanvasRef}
              shop={shop}
              setShop={setShop}
              doors={doors}
              setDoors={setDoors}
              placed={placed}
              setPlaced={setPlaced}
              drains={drains}
              setDrains={setDrains}
              dimensions={dimensions}
              setDimensions={setDimensions}
              selectedDim={selectedDim}
              setSelectedDim={setSelectedDim}
              selected={selected}
              setSelected={setSelected}
              simVehicleKey={simVehicleKey}
              setSimVehicleKey={setSimVehicleKey}
              simDoorId={simDoorId}
              setSimDoorId={setSimDoorId}
              simPathMode={simPathMode}
              setSimPathMode={setSimPathMode}
              simManualWaypoints={simManualWaypoints}
              setSimManualWaypoints={setSimManualWaypoints}
              simManualGoalGear={simManualGoalGear}
              setSimManualGoalGear={setSimManualGoalGear}
              simManualGoalFlip={simManualGoalFlip}
              setSimManualGoalFlip={setSimManualGoalFlip}
              simSpeed={simSpeed}
              setSimSpeed={setSimSpeed}
              selectedDrain={selectedDrain}
              setSelectedDrain={setSelectedDrain}
              onPlacementError={(msg) => showToast(msg)}
              showToast={showToast}
              svgRef={svgRef}
              commitHistory={commitHistory}
              undo={undo}
              redo={redo}
              canUndo={canUndo}
              canRedo={canRedo}
              hideChrome={step === "export"}
              roomDrawMode={roomDrawMode}
              setRoomDrawMode={setRoomDrawMode}
            />
          </div>

          {/* 3D canvas — mounted only when viewMode flips to "3d", lazy-loaded
              so Three.js bundle stays out of initial page load. Unmounting
              when going back to 2D frees WebGL context + GPU memory.
              Known minor quirk: first toggle from 2D may show blank for a
              moment, click 2D then 3D again to refresh. */}
          {viewMode === "3d" && (
            <Suspense
              fallback={
                <div style={{
                  width: "100%", height: "100%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: T.canvasBg, color: T.textDim, fontSize: 13,
                  fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
                }}>
                  Loading 3D view…
                </div>
              }
            >
              <Canvas3D
                shop={shop}
                placed={placed}
                doors={doors}
                drains={drains}
                projectName={exportForm.projectName}
                selected={selected}
                setSelected={setSelected}
                simVehicleKey={simVehicleKey}
                setSimVehicleKey={setSimVehicleKey}
                simDoorId={simDoorId}
                setSimDoorId={setSimDoorId}
                simPathMode={simPathMode}
                simManualWaypoints={simManualWaypoints}
                simManualGoalGear={simManualGoalGear}
                simManualGoalFlip={simManualGoalFlip}
                simSpeed={simSpeed}
                setSimSpeed={setSimSpeed}
              />
            </Suspense>
          )}

          {/* Floating 2D/3D toggle — only shown in build step, not export.
              Sits absolute at bottom-center of this canvas wrapper. */}
          {step === "build" && (
            <ViewModeToggle mode={viewMode} onChange={setViewMode} />
          )}
        </div>
      </div>

      {step === "export" && (
        <ExportPage
          shop={shop}
          doors={doors}
          placed={placed}
          setPlaced={setPlaced}
          commitHistory={commitHistory}
          drains={drains}
          svgRef={svgRef}
          form={exportForm}
          setForm={setExportForm}
          onBack={() => setStep("build")}
        />
      )}

      {step === "help" && <HelpPage />}
    </div>
  );
}
